import { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { novaPoshtaReference, type WarehouseSearchResult } from '../../../services/novaPoshtaClient';

interface NovaPoshtaWarehouseSelectProps {
  apiKey: string | null;
  cityRef: string | null;
  value: WarehouseSearchResult | null;
  onChange: (value: WarehouseSearchResult | null) => void;
  label: string;
  disabled?: boolean;
}

// Fetched once per selected city, then filtered client-side by Autocomplete's default
// matcher — a city has at most a few hundred warehouses, so one call is enough and
// avoids hitting Nova Poshta's rate limit on every keystroke (unlike city search).
export default function NovaPoshtaWarehouseSelect({
  apiKey,
  cityRef,
  value,
  onChange,
  label,
  disabled,
}: NovaPoshtaWarehouseSelectProps) {
  const [options, setOptions] = useState<WarehouseSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!apiKey || !cityRef) {
      setOptions([]);
      return;
    }
    setLoading(true);
    setError(null);
    novaPoshtaReference.getWarehouses(apiKey, cityRef)
      .then(setOptions)
      .catch(() => setError('Не вдалося завантажити список відділень'))
      .finally(() => setLoading(false));
  }, [apiKey, cityRef]);

  return (
    <Autocomplete
      options={options}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      getOptionLabel={(option) => `№${option.number} — ${option.description}`}
      isOptionEqualToValue={(option, val) => option.ref === val.ref}
      loading={loading}
      disabled={disabled || !apiKey || !cityRef}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          fullWidth
          error={!!error}
          helperText={error ?? (!cityRef ? 'Спершу оберіть місто' : undefined)}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
